import { z } from 'zod';

import { staticFeatureFlags } from '@/config/features';
import { ControlType } from '@/constants/institution';
import { institutionIndividualSchema } from '@/features/institution/schemas/individual';
import i18n from '@/lib/i18n';
import { type InstitutionDetailsDecisionMaker as InstitutionDetailsDecisionMakerApi, institutionDetailsAlgorithmSchema } from '@/services/api/profile';
import { toDatePartsFromString, toDateStringFromParts } from '@/utils/date';

const getIndividualsError = () =>
  staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED
    ? i18n.t('institutionDetails.decisionMaker.errors.individualsExactlyOne')
    : i18n.t('institutionDetails.decisionMaker.errors.individuals');

export const institutionFuturesDecisionMakerSchema = z.discriminatedUnion(
  'decisionMaker',
  [
    z.object({
      decisionMaker: z.literal(ControlType.INDIVIDUAL),
      individuals: z
        .array(institutionIndividualSchema, { error: getIndividualsError })
        .min(1, { error: getIndividualsError })
        .max(staticFeatureFlags.INSTITUTION_INDIVIDUAL_FLOW_SOLE_INDIVIDUAL_ENABLED ? 1 : Number.POSITIVE_INFINITY, { error: getIndividualsError }),
    }),
    z.object({
      decisionMaker: z.literal(ControlType.ALGORITHM),
      algorithm: institutionDetailsAlgorithmSchema,
    }),
  ],
  {
    error: i18n.t('institutionDetails.decisionMaker.errors.decisionMaker'),
  },
);

export type InstitutionFuturesDecisionMaker = z.infer<typeof institutionFuturesDecisionMakerSchema>;

export type InstitutionFuturesDecisionMakerOrDefaults = InstitutionFuturesDecisionMaker | Partial<InstitutionFuturesDecisionMaker>;

export const institutionFuturesDecisionMakerToInstitutionDetailsDecisionMakerApi = (decisionMaker: InstitutionFuturesDecisionMaker): InstitutionDetailsDecisionMakerApi => {
  if (decisionMaker.decisionMaker === ControlType.INDIVIDUAL) {
    return {
      ...decisionMaker,
      individuals: decisionMaker.individuals.map((individual) => ({
        ...individual,
        dateOfBirth: toDateStringFromParts(individual.dateOfBirth),
      })),
    };
  }

  return decisionMaker;
};

export const institutionDetailsDecisionMakerApiToInstitutionFuturesDecisionMaker = (decisionMaker: InstitutionDetailsDecisionMakerApi): InstitutionFuturesDecisionMaker => {
  if (decisionMaker.decisionMaker === ControlType.INDIVIDUAL) {
    return {
      ...decisionMaker,
      individuals: decisionMaker.individuals.map((individual) => ({
        ...individual,
        dateOfBirth: toDatePartsFromString(individual.dateOfBirth),
      })),
    };
  }

  return decisionMaker;
};
